import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import periodHeader from 'scoreboard/scorecards/metrics/period-header.hbs'
import AllTags from 'scoreboard/components/all-tags'
import calendars from 'scoreboard/components/calendars'
import formatValue, { shortenedAndUnshortened } from 'scoreboard/components/format-value'
import constants from 'scoreboard/components/constants'
import conductor from 'scoreboard/components/conductor'
import dataTypeResolver from 'scoreboard/components/data-type-resolver'
import InfiniteScroller from 'scoreboard/components/infinite-scroller'
import periodTitles from 'scoreboard/components/period-titles'
import globals from 'scoreboard/components/globals'
import config from 'scoreboard/components/config'
import i18n from 'scoreboard/components/i18n'
import moment from 'moment'
import visible from 'scoreboard/scorecards/visible'
import excelExport from 'scoreboard/components/export/excel-export'
import scorecardReducer from 'scoreboard/components/export/scorecard-item-reducer'
import TooltipAndDetails from 'scoreboard/components/tooltip-and-details'
import nonAbbreviatedValuesTooltip from '../non-abbreviated-values-tooltip.hbs'
import scoreValue from './score-value.hbs'
import guard from 'scoreboard/components/unsaved/guard'
import principals from 'scoreboard/components/principals'
import { getTextVariation, lookup } from 'scoreboard-colors'
const { COLORED_CELL_BG_OPACITY } = constants

function analyzeOptions(options) {
	var periodicOptions = [ 'showActual', 'showScore', 'showGoal', 'showVariance', 'showVariancePercent', 'showTowardGoalPercent' ]
	// keep track of the enabled field names in abbreviated form so we can read
	// them directly from the score.  e.g. 'actual', 'score', etc
	options.periodicFields = _.transform(periodicOptions, function(result, name) {
		if (options[name]) {
			result.push(_.camelCase(name.substring(4)))
		}
	}.bind(this), [])

	if (!config.scoresVisible) {
		_.pull(options.periodicFields, 'score')
	}
	options.series = options.periodicFields.length
	return options
}

var seriesKeysByName = {
	'actual': 'scorecards.node.metric.actualValue',
	'score': 'common.score',
	'goal': 'scorecards.node.metric.threshold.goal',
	'variance': 'report.customReport.variance',
	'variancePercent': 'report.customReport.variancePercentage',
	'towardGoalPercent': 'report.customReport.percentOfGoal'
}

var seriesNames = _.memoize(function(name) {
	return i18n.get(seriesKeysByName[name])
})

var ytdCalendar = _.memoize(function() {
	return calendars.calendarsById[config.ytdCalendarId]
})

function ytdPeriodsAndTitles(periods, titles) {
	// #24660 prevent js error if calendar period was deleted
	if (_.isEmpty(periods)) {
		return {periods: periods, titles: titles}
	}
	var firstPeriodEnd = _.head(periods).end
	var ytdPeriods = ytdCalendar().periods
	// find the first YTD period we want to include
	var ytdPeriodIndex = _.findIndex(ytdPeriods, function(ytdPeriod) {
		return ytdPeriod.end.isSame(firstPeriodEnd) ||
			ytdPeriod.end.isAfter(firstPeriodEnd)
	})
	// if the YTD calendar doesn't extend far enough into the future, we
	// can't include it
	if (ytdPeriodIndex === -1) {
		return { periods: periods, titles: titles }
	}

	var revisedPeriods = []
	var revisedTitles = []
	var periodIndex = 0
	while (periodIndex < periods.length) {
		var period = periods[periodIndex]
		revisedPeriods.push(period)
		revisedTitles.push(titles[periodIndex])
		if (ytdPeriodIndex < ytdPeriods.length) {
			var ytdPeriod = ytdPeriods[ytdPeriodIndex]
			if (period.end.isAfter(ytdPeriod.end) ||
				period.end.isSame(ytdPeriod.end)) {
				revisedPeriods.push(ytdPeriod)
				revisedTitles.push(ytdTitle(ytdPeriod))
				ytdPeriodIndex++
			}
		}
		periodIndex++
	}
	// ensure there's a YTD column at the end, if possible
	if (_.last(revisedPeriods).calendar.id != ytdCalendar().id &&
		ytdPeriodIndex < ytdPeriods.length) {
		revisedPeriods.push(ytdPeriods[ytdPeriodIndex])
		revisedTitles.push(ytdTitle(ytdPeriods[ytdPeriodIndex]))
	}

	return {
		periods: revisedPeriods,
		titles: revisedTitles
	}
}

function ytdTitle(ytdPeriod) {
	return {
		title: i18n.get('common.total'),
		subtitle: ytdPeriod.label
	}
}

var MetricsTable = Backbone.View.extend({

	events: {
		'click tbody a': 'overview',
		'click .tag': 'clickTag'
	},

	initialize: function(options = {}) {
		this.options = options
		this.section = this.options.section || constants.SECTION_SCORECARDS
		this.tableSelector = this.options.tableSelector || '.metrics-table'
		this.scrollEl = this.options.scrollEl
		this.displayOptions(this.options.displayOptions)
	},

	displayOptions: function(opts) {
		if (!opts) return
		this._displayOptions = analyzeOptions(opts)
		return this._displayOptions
	},

	initialData: function(data) {
		this._initialData = data
	},

	stream: function(stream, opts = {}) {
		/**
		 * @type Chunker
		 */
		this._stream = stream
		if (opts.deferSetup) {
			return this._stream
		}
		this._setupStream(stream)
		return this._stream
	},

	render: function () {
		// record this once so it doesn't need to be looked up over and over
		// in this.renderItem
		this.table = this.$(this.tableSelector)
		this.setupRenderState(this.table)
		this._setupStream()

		// Each time data is rendered (initially and after the infinite scroller loads more)
		// we want to setup the rawValuesTooltip
		this.on('dataRendered', () => {
			this.rawValuesTooltip && this.rawValuesTooltip.remove()
			this.rawValuesTooltip = new TooltipAndDetails({
				el: this.el,
				selector: '[data-unshortened-value]',
				tooltipContent: (e) => {
					return nonAbbreviatedValuesTooltip({
						header: e.target.dataset.unshortenedHeader,
						value: e.target.dataset.unshortenedValue
					})
				},
				tooltipOptions: {
					extraClass: 'tooltip-and-details non-abbreviated-tooltip metrics-table-performance-non-abbreviated-tooltip'
				},
				preventTooltip: this.options.preventTooltip
			})
		})

		return this
	},

	setupRenderState: function (table) {
		this.tableHead = table.find('thead')
		this.tableBody = table.find('tbody')
		// Used to keep the raw data and types for excel exports
		this.exportDataModel = {}
		this.tableBody.empty()

		this._numMetrics = 0
		// whether this is the very first node to come down, which is the one
		// we use for the title but don't want to display in the table
		this._firstSeen = false
		// initially the structure cell will have a width of 1
		this._structureColspan = 1
		// keeps track of the last cells in the header and body that may need
		// their rowspans adjusted if it turns out the structure column needs
		// to be wider
		this._lastStructureCells = []
		// keep track of ancestors so we can draw the structure column
		this._ancestors = []
		this._ancestorEls = []
		this._lastAncestorEls = []
		this._lastAncestors = []
		// rows that may be added to the metrics report (nodes that don't have
		// any metric descendants should be omitted from the report)
		this._possibleRows = []
	},

	resetStream: function () {
		delete this._stream
	},

	_setupStream: function() {
		if (!this._stream) {
			return
		}
		this.renderHeaders()
		this.tableType()
		this.scrollEl.off()	//remove any previous listeners
		this.scrollEl.on('scroll', this.trigger.bind(this, 'scroll'))
		this.scroller = new InfiniteScroller({
			el: this.scrollEl
		})
		this.scroller.on('scroll', this._stream.next.bind(this._stream))

		// This has to be registered before calls to checkScroll() or data will
		// not be rendered
		this._stream.on('data', this.renderData.bind(this))

		if (this._initialData) {
			this.renderData(this._initialData)
			//handle case when initial chunk is small enough to fit on the screen
			//but there is more we can display, fill out until we require a scroll
			//see infinite-scroller.dataReady for more details
			this._stream.on('data', this.scroller.dataReady.bind(this.scroller))
			this.scroller.checkScroll()
			this.table.toggleClass('is-not-displayed', false)
		} else {
			this._stream.once('data', function() {
				console.log('First chunk of data received after ' + moment().diff(this.options.start) + 'ms')
				this.table.toggleClass('is-not-displayed', false)
				this.trigger('visible')
			}.bind(this))
			this._stream.on('data', this.scroller.dataReady.bind(this.scroller))
		}

		// don't render the headers until we have some data, or else the browser
		// renders headers without any data first, and it doesn't look good
		this._stream.once('data', function() {
			console.log('Finished drawing report after ' + moment().diff(this.options.start) + 'ms')
			_.defer(function() {
				console.log('Browser done rendering after ' + moment().diff(this.options.start) + 'ms')
			}.bind(this))
		}.bind(this))

		// Chunker has processed all rows (and they're loaded into DOM)
		if (this._stream.endEmitted) {
			this._onStreamEnd()
			return
		} else {
			this._stream.on('end', this._onStreamEnd.bind(this))
		}

		this._stream.resume()
		if (this._stream.endEmitted) {
			this._onStreamEnd()
		}
	},

	_onStreamEnd: function() {
		//#25525 only need to call this if the last item was a metric, otherwise
		//it causes the last row to have an incorrect rowspan
		if (this._wasLastItemMetric) {
			this.fixPreviousRow()
		}
		this.trigger('streamEnd', this._numMetrics)
	},

	renderData: function(data) {
		var tbody = this.tableBody
		_.each(data, function(item) {
			var data = this.buildItemHTML(item)
			tbody.append(data.rowsToAppendToTable, data.periodicDataHTML)

			//we only need to do this if showing parents, we must not do when
			//not showing parents because that screws up the sorting
			if (this._displayOptions.showParents) {
				this.fixStructure(data.parent)
			}

			if (item.type === 'metric') {
				this._numMetrics++
			}
			this.exportDataModel[item.id] = scorecardReducer(item)
		}.bind(this))
		this.$('.has-multiple-rows').last().addClass('last-row-group')

		// For tables with multiple series represented, but no structure (parents)
		// shown, we want to alternate table row colors by series. Do that here.
		if (this._displayOptions.series > 0 && !this._displayOptions.showParents) {
			var rows = this.$('tr[data-id]')
			var striped = false
			var tempRow
			// Iterate over and stripe all rows that start a series
			for (var i = 0; i < rows.length; i++) {
				rows.eq(i).toggleClass('table-row-striped', striped)
				tempRow = rows.eq(i).next()
				// Iterate over and stripe the neighboring rows according how many
				// rows each series takes up
				for (var j = 0; j < this._displayOptions.series; j++) {
					tempRow.toggleClass('table-row-striped', striped)
					tempRow = tempRow.next()
				}
				striped = !striped
			}
		}

		// Wait till the table is finished rendering before initially positioning
		// the display options
		process.nextTick(function() {
			this.trigger('dataRendered')
		}.bind(this))
	},

	// given a stream, listens until a metric is found and then draws
	// appropriate column headers
	renderHeaders: function() {
		let sortableTableHeaderFields = []

		// empty the old headers in case re-rendering!
		var row = $('<tr class="table-row-solid"></tr>')
		this.tableHead.empty().append(row)
		var html = ''
		// id
		if (this._displayOptions.showId) {
			// html not used for the ID because we need
			html += '<th>' + _.escape(i18n.get('common.id')) + '</th>'
			sortableTableHeaderFields.push('id')
		}
		// structure
		html += '<th class="structure">' +
			_.escape(i18n.get('report.metricsReport.column.metric')) +
			'</th>'
		sortableTableHeaderFields.push('label')
		// owners
		if (this._displayOptions.showOwners) {
			html += '<th>' + _.escape(i18n.get('common.owners')) + '</th>'
			sortableTableHeaderFields.push('owners')
		}
		// updaters
		if (this._displayOptions.showUpdaters) {
			html += '<th>' + _.escape(i18n.get('common.updaters')) + '</th>'
			sortableTableHeaderFields.push('updaters')
		}
		// initiatives
		if (this._displayOptions.showInitiatives) {
			html += '<th>' + _.escape(i18n.get('initiatives.initiatives')) + '</th>'
			sortableTableHeaderFields.push('initiatives')
		}
		// tags
		if (this._displayOptions.showTags) {
			html += '<th>' + _.escape(i18n.get('report.customReport.tags')) + '</th>'
			sortableTableHeaderFields.push('tags')
		}
		// calendar
		if (this._displayOptions.showCalendar) {
			html += '<th>' + _.escape(i18n.get('common.calendar.calendar')) + '</th>'
			sortableTableHeaderFields.push('calendar')
		}
		// impact
		if (this._displayOptions.showImpact) {
			html += '<th data-value-field="impact">' + _.escape(i18n.get('report.scorecardReport.column.influence')) + '</th>'
			sortableTableHeaderFields.push('impact')
		}
		// series
		if (this._displayOptions.series > 1) {
			html += '<th>' + _.escape(i18n.get('scorecards.node.metric.series')) + '</th>'
			//the null is the skip this header for sorting
			sortableTableHeaderFields.push(null)
		}
		// periods (including YTD)
		if (this._displayOptions.series > 0 && this._displayOptions.periods > 0) {
			var numPeriods = this._displayOptions.periods
			var refPeriodId = this._displayOptions.referencePeriodId || globals.periodId
			var periods = calendars.relativePeriods(refPeriodId, -1 * (numPeriods - 1), 0)
			var titles = periodTitles(periods)

			if (this.showYtd(refPeriodId)) {
				var revised = ytdPeriodsAndTitles(periods, titles)
				// integrate YTD periods and titles directly into the period
				// and titles array so they can be processed like any other
				// period
				periods = revised.periods
				titles = revised.titles
			}

			this._calendarPeriods = periods

			_.each(titles, function(context, i) {
				context = _.clone(context)
				// No double-quotes here, handlebars inserts them,
				// if we add them we get double the double-quotes, not good
				context.thAttributes = ' data-period=' + periods[i].id
				sortableTableHeaderFields.push({field: this._displayOptions.periodicFields[0], period: periods[i].id})
				if (periods[i].calendar.id == ytdCalendar().id) {
					context.thAttributes += ' class=total'
				}
				html += periodHeader(context)
			}.bind(this))
		}

		row.html(html)

		this.trigger('sortableTableHeaderFields', sortableTableHeaderFields)

		// keep a reference to the structure cell
		this._lastStructureCells.push(row.find('.structure'))

		this.$('th').toggleClass('is-sortable', !this._displayOptions.showParents)
	},

	// Issue #8970: When certain options are displayed, the table can potentially have td's
	// that are very tall and look odd if the content is vertically centered. So to fix that,
	// when these options are selected, we will attach `.top-aligned-table` to align the content
	//to the top of the tds.
	tableType: function() {
		var options = this._displayOptions

		// If any of these options are selected, the table can potentially be tall.
		if ((options['showOwners'] || options['showUpdaters'] || options['showParents'] || options['showInitiatives'] || options['showTags']) === true) {
			this.$('.metrics-table').addClass('top-aligned-table')
			return
		}

		// If more than one of the following options are selected, table could be tall.
		var optionsPair = ['showActual', 'showScore', 'showGoal', 'showVariance', 'showVariancePercent']
		var total = 0

		_.each(optionsPair, function(option) {
			if (options[option]) {
				total += 1
			}
		})

		if (total > 1) {
			this.$('.metrics-table').addClass('top-aligned-table')
		} else {
			// If it gets this far, remove the class so it vertically centers content
			this.$('.metrics-table').removeClass('top-aligned-table')
		}
	},

	isMetric: function(item) {
		// type isn't sent unless it's required to display the results
		return _.isUndefined(item.type) || item.type === 'metric'
	},

	// determines whether YTD columns should be displayed
	showYtd: function(referencePeriodId) {
		return this._displayOptions.showYtd && ytdCalendar() &&
			calendars.periodsById[referencePeriodId].calendar.avgPeriodSize < 300
	},

	/**
	 * Given a scorecard item, a hash of html structures are returned that
	 * can be used to update the DOM or generate export data
	 *
	 * This function must not produce any DOM manipulation side effects
	 *
	 * @param item
	 * @returns {{parent: JQuery, periodicDataHTML: string, rowsToAppendToTable: JQuery[]}}
	 */
	buildItemHTML: function(item) {
		this._wasLastItemMetric = this.isMetric(item)
		let usingDefaultSort = _.get(this.options, 'sort.sortField', null) == null

		var data = {
			parent: undefined,
			periodicDataHTML: '',
			rowsToAppendToTable: []
		}

		// don't show an entire column just for the name of the root of the
		// subtree, because that name is already at the top of the display
		// pane
		if (!this._firstSeen && !this.isMetric(item)) {
			this._firstSeen = true

			//when not using the default sort we can't be sure the order of
			//data matches the tree order, which the initial layout algorithm
			//assumed. In this case it's not necessary and actually problematic
			//to do this
			if (usingDefaultSort) {
				this._ancestors.push(item)
			}
			return data
		}
		// walk up the tree to the correct parent
		var differentParent = false
		if (item.parentId && this._ancestors.length > 0) {
			let lastItem = _.last(this._ancestors)

			let removePreviousChildlessNonMetricNode = () => {
				if (this._row) {
					//we're a node whose previous item was a childless's node, so remove
					//all the previous processing since that previous item should be hidden
					$(_.last(this._row.find('td'))).remove()
				}
			}

			if (item.parentId == lastItem.parentId) {
				this._ancestors.pop()
				this._ancestorEls.pop()
				removePreviousChildlessNonMetricNode()
			} else if (item.parentId != lastItem.id) {
				removePreviousChildlessNonMetricNode()
				differentParent = true
				while(this._ancestors.length > 0 && item.parentId != _.last(this._ancestors).id) {
					this._ancestors = _.initial(this._ancestors)
					this._ancestorEls = _.initial(this._ancestorEls)
				}
			}
		}
		data.parent = _.last(this._ancestors)

		// if we moved to a different parent without writing the possible rows
		// and clearing them out, it means the rows that weren't written
		// out were for non-metrics with no metric descendants, and should
		// not be rendered.  so clear them out here, and below do NOT call
		// .fixPreviousRow (or else you get issue #4346)
		var noMetricChildren = differentParent && this._possibleRows.length
		if (noMetricChildren) {
			this._possibleRows = []
		}

		if (!this._row || differentParent) {
			const requiresAncestry = this._row && this._ancestors.length > 0 && _.last(this._ancestors).id == item.parentId
			if (requiresAncestry) {
				// #14416 - The current item has common ancestors from previous non-metric nodes.
				// They must remain intact for the structure to be correct
				const parentIndex = this._ancestors.length - 1
				// Remove all non-metric items previously added
				this._row.children().slice(parentIndex).remove()
			} else { // start a new row
				var html = '<tr></tr>'
				var firstField = this._displayOptions.periodicFields[0]
				if (firstField) {
					html = '<tr data-value-field="' + firstField + '"></tr>'
				}
				this._row = $(html)
			}
			this._possibleRows.push(this._row)

			// #14416 #15227 don't call this._row.append(this._ancestorEls) here
			if (!noMetricChildren) {
				this.fixPreviousRow()
			}
		}

		var rowspan = this._displayOptions.series < 2 ? '' :
			' rowspan="' + this._displayOptions.series + '"'

		this._row.toggleClass('has-multiple-rows', !_.isNil(rowspan))

		if (this.isMetric(item) && this._displayOptions.showId) {
			this._row.prepend('<td' + rowspan + ' class="id">' + item.id + '</td>')
		}

		var leftmost = this._ancestorEls.length === 0 && !this._displayOptions.showId
		var cellClasses = leftmost ? 'leftmost' : ''
		if (this.isMetric(item)) {
			cellClasses += (cellClasses ? ' ' : '') + 'metric'
		}
		// add a cell containing the node label
		var td = this._lastStructureCell =
			$('<td' + rowspan + (cellClasses ? ' class="' + cellClasses + '"' : '') +
				'><div><a data-id="' + item.id + '">' +
				_.escape(item.label) +
			'</a></div></td>')
			.appendTo(this._row)
		this._row.attr('data-id', item.id)
		if (!this.isMetric(item)) {
			// running total of found child metrics, to make rowspan
			// calculations easier
			td.data('child-metrics', 0)
		}

		this.indentSubmetrics(item, data.parent, td)

		if (this.isMetric(item)) {
			data.rowsToAppendToTable = this._possibleRows
			this.buildNonPeriodicDataHTML(item, rowspan)
			data.periodicDataHTML = this.buildPeriodicDataHTML(item)

			this._possibleRows = []
			this._row = null
		}

		// #25816, #32979 ancestors are only needed if sub metrics or parents are present,
		// but they're needed for every metric since the first one may not have
		// a parentId defined, but is in fact an ancestor of it's own sub metrics.
		const {showParents, showSubMetrics} = this._displayOptions
		if (showParents || showSubMetrics) {
			this._lastAncestors = _.clone(this._ancestors)
			this._lastAncestorEls = _.clone(this._ancestorEls)
			this._ancestors.push(item)
			this._ancestorEls.push(td)
		}

		return data
	},

	fixPreviousRow: function() {
		// increase the rowspan of parents, if needed
		_.each(this._lastAncestorEls, function(ancestorEl, i) {
			// if the ancestor is a non-metric that has child metrics which
			// cause its rowspan to need adjustment
			var childMetrics = ancestorEl.data('child-metrics')
			if (_.isNumber(childMetrics)) {
				ancestorEl.data('child-metrics', ++childMetrics)
				if (childMetrics > 1) {
					// don't let a lack of periodic data set all the rowspans to 0!
					ancestorEl.attr('rowspan', Math.max(1, this._displayOptions.series) * childMetrics)
				}
			}
		}.bind(this))
		// remember the last structure cell, in case we discover the
		// structure column is larger than we thought
		this._lastStructureCell && this._lastStructureCells.push(this._lastStructureCell)
		// set colspan based on current known width of structure cells
		var nonMetricParents = _.reduce(this._lastAncestors, function(result, ancestor) {
			return result + (!this.isMetric(ancestor) ? 1 : 0)
		}.bind(this), 0)
		// if we are showing parents
		if (this._structureColspan > 1) {
			// the first ancestor wasn't actually shown in the report so it
			// doesn't count
			nonMetricParents--
		}
		if (nonMetricParents < this._structureColspan - 1) {
			this._lastStructureCell.attr('colspan', this._structureColspan - nonMetricParents)
		}
	},

	// adds indent to the cell, if the item is a submetric
	indentSubmetrics: function(item, parent, td) {
		if (parent && this.isMetric(item) && this.isMetric(parent)) {
			var parentEl = _.last(this._ancestorEls)
			var indent = parentEl.data('indent')
			indent = indent ? 1 + parseInt(indent) : 1
			if (!this._tdPaddingLeft) {
				this._tdPaddingLeft = parseInt(parentEl.css('padding-left'))
			}
			var padding = this._tdPaddingLeft + 25 * indent
			td.data('indent', indent).css('padding-left', padding + 'px')
		}
	},

	// renders all the non-periodic data for a metric, which is the columns
	// between the structure column and the periodic data
	buildNonPeriodicDataHTML: function(item, rowspan) {
		var html = ''
		// owners
		if (this._displayOptions.showOwners) {
			html += `<td class="names principal-list" ${rowspan}>${principals.toHtml(item.owners)}</td>`
		}
		// updaters
		if (this._displayOptions.showUpdaters) {
			html += `<td class="names principal-list" ${rowspan}>${principals.toHtml(item.updaters)}</td>`
		}
		// initiatives
		if (this._displayOptions.showInitiatives) {
			html += `<td ${rowspan} class="initiatives principal-list">`
			_.each(item.initiatives, function(initiative) {
				html += `<div><a data-id="${initiative.id}" data-section="initiatives">${_.escape(initiative.label)}</a></div>`
			})
			html += '</td>'
		}
		// tags
		if (this._displayOptions.showTags) {
			html += '<td' + rowspan + '>'
			_.each(item.tags, function(tag) {
				html += '<div class="tag">' + _.escape(tag) + '</div>'
			})
			html += '</td>'
		}
		// calendar
		if (this._displayOptions.showCalendar) {
			var cal = _.get(calendars.calendarsById[item.calendarId], 'label', '')
			html += '<td' + rowspan + ' class="calendar">' + _.escape(cal) + '</td>'
		}
		// impact
		if (this._displayOptions.showImpact) {
			html += '<td' + rowspan + ' class="impact">'
				+ formatValue({ value: item.impact, dataType: 'percent', precision: item.precision })
				+ '</td>'
		}
		html && this._row.append(html)
	},

	// renders all periodic data
	buildPeriodicDataHTML: function(item) {
		if (this._displayOptions.periodicFields.length > 0) {
			var name = this._displayOptions.periodicFields[0]
			var html = ''
			if (this._displayOptions.periodicFields.length > 1) {
				html += '<td class="series-name">' + _.escape(seriesNames(name)) + '</td>'
			}
			html += this.periodicDataHtml(item, name, true)
			this._row.append(html)
		}

		var html = ''
		for (var i=1; i<this._displayOptions.periodicFields.length; i++) {
			var name = this._displayOptions.periodicFields[i]
			html += '<tr data-value-field="' + name + '">'
			html += '<td class="series-name">' + _.escape(seriesNames(name)) + '</td>'
				+ this.periodicDataHtml(item, name, false) + '</tr>'
		}
		return html
	},

	// returns HTML for a single series of periodic data
	periodicDataHtml: function(item, fieldName, showColors) {
		var scoresByPeriod = _.keyBy(item.scores, function(score) {
			return score.periodId
		})

		var html = ''
		_.each(this._calendarPeriods, function(period) {
			var score = scoresByPeriod[period.id]
			html += this.scoreHtml(period, item, score, fieldName, showColors)
		}.bind(this))
		return html
	},

	/**
	 *
	 * @param period for which to render the score field
	 * @param {object} item the scorecard item
	 * @param {object} score
	 * @param fieldName the score field to display (actual, score, etc)
	 * @param {boolean} showColors to color the value or not
	 * @returns {string} the html for the cell (td) of score having fieldName
	 */
	scoreHtml: function(period, item, score, fieldName, showColors) {
		var color = (score && score.color) ? score.color : 'gray'
		var v = visible(item, period)
		var html = '<td class="'
		if (period.calendar.id == ytdCalendar().id) {
			html += 'total '
		}
		if (showColors && v) {
			let hex = lookup(color).color
			let textVariation =
				getTextVariation(hex, COLORED_CELL_BG_OPACITY)
			html += `triangle ${textVariation} label-on-colored-background` +
			 ` color ${color}`
		} else {
			html += 'no-color'
		}
		html += '">'
		if (score && v) {
			if (score.applicable === false) {
				html += i18n.get('scorecards.node.metric.notapplicable')
			} else {

				var { shortened, unshortened } = shortenedAndUnshortened({
					value: score[fieldName],
					dataType: dataTypeResolver(score.dataType, fieldName),
					currencyCode: item.currencyCode,
					// #32434 score should always use default decimal precision
					precision: fieldName === 'score' ? null : item.precision
				})

				html += scoreValue({
					unshortened: this._displayOptions.showAbbreviatedValues && (shortened != unshortened) ? unshortened : null,
					value: this._displayOptions.showAbbreviatedValues ? shortened : unshortened,
					period: period.label
				})
			}
		} else {
			html += '<div class="color-wrapper"></div>'
		}
		html += '</td>'
		return html
	},

	// make sure the structure column is laid out property by adjusting
	// colspans as necessary
	fixStructure: function(parent) {
		// increase ALL structure colspans by one if needed
		if (this._ancestorEls.length > this._structureColspan && !this.isMetric(parent)) {
			_.each(this._lastStructureCells, function(cell) {
				cell.attr('colspan', function(i, cs) {
					// increase colspan by one, or if there isn't a colspan yet
					// assign it to 2
					return cs ? parseInt(cs) + 1 : 2
				})
			})
			this._structureColspan++
			if (this._structureColspan > 1) {
				this.$('th.structure').text(i18n.get('report.metricsReport.column.structure'))
			}
		}
	},

	overview: function(e) {
		var id = $(e.target).data('id')
		var section = $(e.target).data('section') || 'scorecards'

		let gotoOverview = () => {
			conductor.overview(section, id)
		}

		if (this.section != section) {
			guard.conditionallyRun(gotoOverview)
		} else {
			gotoOverview()
		}
	},

	clickTag: AllTags.display,

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.header, this.scroller, this.displayOptions, this.rawValuesTooltip], 'remove')
	},

	exportTable: function(label) {
		var opts = {
			label: label,
			dataAttributes: {
				// period and id used to select the score from the model
				th: 'data-period',
				tr: 'data-id',
				// the score attribute to use for a given row (actual, score, goal, etc)
				value: 'data-value-field'
			},
			model: this.exportDataModel
		}
		return excelExport.saveTableAsFile(this.tableBody, opts)
	}

})

export default MetricsTable
export { ytdPeriodsAndTitles }

MetricsTable.__filename__ = 'scorecards/metrics/metrics-table.js'
